import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Search, Download, Upload, Trash2, FileText, Printer,
  Plus, AlertTriangle, Truck, Loader2, Eye, Edit3, Warehouse,
  ChevronDown, CheckCircle2, Ticket, XCircle,
} from "lucide-react";
import { api, API_BASE_URL, downloadBlob, tokenStore, blobErrorMessage } from '@/api/client';
import { B2C_TABS, B2B_TABS, STATUS_LABEL, STATUS_COLOR, ALL_SHIPPED_STATUS_IDS } from './constants';
import { OrderUploadDialog } from './OrderUploadDialog';
import { ChooseCourierDialog } from './ChooseCourierDialog';
import { useBulkProgress, BulkProgressOverlay, SHIP_CHUNK_SIZE, CHUNK_TIMEOUT_MS } from './bulk-progress';
import { AssignWarehouseDialog } from './AssignWarehouseDialog';
import { TrackingDrawer } from './TrackingDrawer';
import { Modal } from '@/components/ui/Modal';
import { SignedFileLink } from '@/components/ui/SignedImage';
import { DateRangePicker, DatePreset } from '@/components/ui/DateRangePicker';
import { CopyText } from '@/components/ui/CopyText';
import { CallButton } from '@/components/order/CallButton';
import { CallRecordingsButton } from '@/components/order/CallRecordingsButton';
import { MultiSelectFilter } from '@/components/ui/MultiSelectFilter';
import { TableSkeleton } from '@/components/ui/Skeleton';

interface Order {
  id: string; orderNo: string; invoiceNo?: string | null; invoiceNosAll?: string[]; awbNo?: string; lrNo?: string; pickupId?: string; courierBookingId?: string;
  orderType: 'B2C' | 'B2B'; statusId: number; jobId?: string | null;
  deliveryType: 'COD' | 'PREPAID'; bookingDate: string; createdAt?: string; ewayBillNo?: string | null; lastBookingError?: string | null; shipDate?: string | null; cancelledAt?: string | null; cancelledById?: number | null; cancelledByName?: string | null;
  customerName: string; customerMobile?: string | null; customerCity: string; customerState: string; customerPincode: string;
  invoiceValue: string; codValue: string;
  createdByName?: string | null;
  merchant?: { id: number; companyName: string };
  courier?: { id: number; name: string; code?: string; brand?: string };
  warehouse?: { id: number; name: string; code?: string; city?: string };
  warehouseId?: number;
  freight?: { total?: string | number | null; subtotal?: string | number | null; totalGst?: string | number | null; isManualOverride?: boolean } | null;
  podFile?: string;
  totalAttempts?: number;
  deliveredAt?: string | null; rtoDeliveredAt?: string | null;
  deliveredAtResolved?: string | null; rtoDeliveredAtResolved?: string | null;
  _count?: { ndrs?: number };
}

interface Merchant { id: number; companyName: string }
interface Courier { id: number; name: string; orderType: 'B2C' | 'B2B' }

interface Props { orderType: 'B2C' | 'B2B' }

// Columns offered in the Export column-chooser. `key` must match the backend export column keys.
const EXPORT_COLUMNS: { key: string; label: string }[] = [
  { key: 'orderNo', label: 'Order No' },
  { key: 'systemOrderNo', label: 'System Order No' },
  { key: 'courierOrderRef', label: 'Courier Order Ref' },
  { key: 'awbNo', label: 'AWB No' },
  { key: 'awbOrLr', label: 'LR / AWB No (one column)' },
  // No LR column here on purpose: `lrNo` is the B2B consignment number and is null on every one of the
  // 52,429 live B2C orders, so it only ever exported an empty column.
  { key: 'orderType', label: 'Order Type' },
  { key: 'status', label: 'Status' },
  { key: 'deliveryType', label: 'Delivery Type' },
  { key: 'qty', label: 'Shipment Quantity' },
  { key: 'merchant', label: 'Merchant' },
  { key: 'courier', label: 'Courier' },
  { key: 'customerName', label: 'Customer Name' },
  { key: 'customerMobile', label: 'Customer Mobile' },
  { key: 'customerCity', label: 'Customer City' },
  { key: 'customerState', label: 'Customer State' },
  { key: 'customerPincode', label: 'Customer Pincode' },
  { key: 'productName', label: 'Product Name' },
  { key: 'warehouseName', label: 'Pickup Location' },
  { key: 'bookingCity', label: 'Booking City' },
  { key: 'bookingState', label: 'Booking State' },
  { key: 'pickupPincode', label: 'Pickup Pincode' },
  { key: 'invoiceValue', label: 'Invoice Value' },
  { key: 'codValue', label: 'COD Value' },
  { key: 'chargeableWeight', label: 'Chargeable Weight' },
  { key: 'totalWeight', label: 'Weight' },
  { key: 'volumetricWeight', label: 'Volumetric Weight' },
  { key: 'productDim', label: 'Dimensions' },
  { key: 'chargedWeightDims', label: 'Charged Weight (with Dimensions)' },
  { key: 'totalBoxes', label: 'No. of Boxes' },
  { key: 'invoiceNo', label: 'Invoice No.' },
  { key: 'totalAttempt', label: 'No. of Attempts' },
  { key: 'bookingDate', label: 'Booking Date' },
  { key: 'shipDate', label: 'Ship Date' },
  { key: 'edd', label: 'EDD' },
  { key: 'lastEventDate', label: 'Last Event Date' },
  { key: 'deliveredDate', label: 'Delivered Date' },
  { key: 'rtoDeliveredDate', label: 'RTO Delivered Date' },
  // `createdById` on the order — the person who uploaded / created it, whether that was the panel,
  // an Excel upload or the API. Labelled "Uploaded By" rather than "Booked By": the booking is the
  // courier call, and on an imported sheet the two are routinely different people.
  { key: 'userId', label: 'Uploaded By (User ID)' },
  { key: 'userName', label: 'Uploaded By (User Name)' },
];

// B2B export column-chooser — the exact set + sequence ops asked for. `key` must match the backend
// COLS registry keys in orders.service.ts (exportCsv).
const B2B_EXPORT_COLUMNS: { key: string; label: string }[] = [
  { key: 'merchantId', label: 'Merchant ID' },
  { key: 'merchantName', label: 'Merchant Name' },
  { key: 'brandName', label: 'Brand Name' },
  { key: 'salePerson', label: 'Sale Person' },
  { key: 'masterAwb', label: 'Master AWB' },
  { key: 'lrNumber', label: 'LR Number' },
  { key: 'awbOrLr', label: 'LR / AWB No (one column)' },
  { key: 'status', label: 'Status' },
  { key: 'lastMsg', label: 'Last MSG' },
  { key: 'lastEventDate', label: 'Last Event Date' },
  { key: 'deliveredDate', label: 'Delivered Date' },
  { key: 'rtoDeliveredDate', label: 'RTO Delivered Date' },
  { key: 'totalAttempt', label: 'Total Attempt' },
  { key: 'courier', label: 'Courier' },
  { key: 'customerName', label: 'Customer Name' },
  { key: 'customerAddress', label: 'Customer Address' },
  { key: 'customerCity', label: 'Customer City' },
  { key: 'customerState', label: 'Customer State' },
  { key: 'customerPincode', label: 'Customer Pincode' },
  { key: 'customerPhone', label: 'Customer Phone' },
  { key: 'warehouseName', label: 'Warehouse Name' },
  { key: 'bookingPincode', label: 'Booking Pincode' },
  { key: 'bookingState', label: 'Booking State' },
  { key: 'bookingCity', label: 'Booking City' },
  { key: 'deliveryType', label: 'Delivery Type' },
  { key: 'pickupType', label: 'Pickup Type' },
  { key: 'freightCollectionType', label: 'Freight Collection Type' },
  { key: 'lrGenerate', label: 'LR Generate' },
  { key: 'totalBoxes', label: 'Total Boxes' },
  { key: 'productName', label: 'Product Name' },
  { key: 'productDim', label: 'Product Dim.' },
  { key: 'invoiceValue', label: 'Invoice Value' },
  { key: 'collectableAmount', label: 'Collectable Amount' },
  { key: 'totalWeight', label: 'Total Weight' },
  { key: 'volumetricWeight', label: 'Volumetric Weight' },
  { key: 'chargedWeight', label: 'Charged Weight' },
  { key: 'chargedWeightDims', label: 'Charged Weight (with Dimensions)' },
  { key: 'podFile', label: 'POD File' },
  { key: 'invoiceNo', label: 'Invoice No.' },
  { key: 'ewayBillNo', label: 'Eway Bill No.' },
  { key: 'shipmentAgeing', label: 'Shipment Ageing' },
  { key: 'userId', label: 'Uploaded By (User ID)' },
  { key: 'userName', label: 'Uploaded By (User Name)' },
  { key: 'zone', label: 'Zone' },
  { key: 'edd', label: 'EDD' },
  { key: 'created', label: 'Created' },
  { key: 'updated', label: 'Updated' },
  { key: 'lastRemarks', label: 'Last Remarks' },
  { key: 'remarksAddedBy', label: 'Remarks Added By' },
  { key: 'assignedTo', label: 'Assigned To' },
  { key: 'assignedAt', label: 'Assigned At' },
  { key: 'scanRemarks', label: 'Scan Remarks' },
];

interface ShipResult {
  ok: number;
  failed: number;
  total?: number;
  errors: Array<{ id: string; orderNo?: string; invoiceNo?: string; orderType?: string; source: 'system'|'courier'; error: string }>;
}

export function OrdersListPage({ orderType }: Props) {
  const qc = useQueryClient();
  const nav = useNavigate();

  // Re-fetch the order list + tab counts (B2C or B2B) right away. Called after EVERY ship/reship —
  // success OR failure — because a failed booking still moves the order to Booking-Failed, changing
  // both the list and the tab counts. invalidate marks stale; refetch forces an immediate re-call of
  // whatever list/counts are currently on screen.
  const isOrdersQuery = (q: any) => Array.isArray(q.queryKey) && (q.queryKey[0] === 'orders' || q.queryKey[0] === 'order-tab-counts');
  const refreshOrders = () => {
    qc.invalidateQueries({ predicate: isOrdersQuery });
    qc.refetchQueries({ type: 'active', predicate: isOrdersQuery });
  };

  // Own-courier actions
  const bookMut = useMutation({
    mutationFn: async (orderId: string) => (await api.post(`/orders/${orderId}/book-waybill`)).data,
    onError: (err: any, orderId) => {
      // Single-order book also surfaces an error modal for consistency
      const msg = err?.response?.data?.message ?? 'Booking failed';
      const o = items.find((x: any) => x.id === orderId);
      const source: 'system' | 'courier' = msg.toLowerCase().includes('booking failed') && (msg.includes('rejected') || msg.includes(':')) ? 'courier' : 'system';
      setShipResult({ ok: 0, failed: 1, errors: [{ id: orderId, orderNo: o?.orderNo, invoiceNo: o?.ewayBillNo ?? undefined, orderType: o?.orderType, source, error: msg }] });
    },
    onSuccess: (data: any) => {
      const g = String(data.generated ?? '');
      if (g.startsWith('pending:')) toast.message('Booking queued — looking for LR from the courier…');
      else toast.success(`Waybill generated: ${data.generated ?? data.awbNo ?? data.lrNo}`);
    },
    // Refresh list + counts whether the booking succeeded or failed.
    onSettled: () => refreshOrders(),
  });
  const [cancelPwModalOpen, setCancelPwModalOpen] = useState(false);
  const [cancelTargetId, setCancelTargetId] = useState<string | null>(null);
  const [cancelPw, setCancelPw] = useState('');
  const [cancelPwBusy, setCancelPwBusy] = useState(false);
  const cancelMut = useMutation({
    mutationFn: async (arg: { orderId: string; password: string }) => {
      // Verify password first
      const v = await api.post('/auth/verify-password', { password: arg.password });
      if (!(v.data as any)?.ok) throw new Error('Incorrect password');
      return (await api.post(`/orders/${arg.orderId}/cancel-waybill`)).data;
    },
    onSuccess: () => {
      toast.success('Waybill cancelled');
      // Invalidate ANY 'orders'-keyed list query (filter args vary per tab/page) + order detail + tab counts
      qc.invalidateQueries({ predicate: (q: any) => Array.isArray(q.queryKey) && (q.queryKey[0] === 'orders' || q.queryKey[0] === 'order-tab-counts') });
      qc.invalidateQueries({ predicate: (q: any) => Array.isArray(q.queryKey) && q.queryKey[0] === 'order' });
      // Re-fetch the active list immediately (don't wait for next focus)
      qc.refetchQueries({ predicate: (q: any) => Array.isArray(q.queryKey) && q.queryKey[0] === 'orders' && q.state.status !== 'idle' });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Cancel failed'),
  });

  const bookApptMut = useMutation({
    mutationFn: async (id: string) => {
      const date = window.prompt('Appointment Date (YYYY-MM-DD)?');
      if (!date) throw new Error('Cancelled');
      const time = window.prompt('Appointment Time (HH:MM, optional)?') || undefined;
      return (await api.post(`/orders/${id}/appointment/book`, { appointmentDate: date, appointmentTime: time })).data;
    },
    onSuccess: (r: any) => toast.success(`Appointment booked: ${r.appointmentId ?? 'OK'}`),
    onError: (e: any) => {
      if (e?.message === 'Cancelled') return;
      toast.error(e?.response?.data?.message ?? e?.message ?? 'Failed to book appointment');
    },
  });

  const TABS = orderType === 'B2C' ? B2C_TABS : B2B_TABS;
  const [tab, setTab] = useState<string>(TABS[0].id);
  // Multi-status filter for the "All Shipped Orders" tab (searchable, multi-select).
  const [statusIds, setStatusIds] = useState<(string | number)[]>([]);
  const [trackOrderId, setTrackOrderId] = useState<string | number | null>(null);
  const [searchField, setSearchField] = useState<'awbNo' | 'lrNo' | 'orderNo' | 'customerMobile' | 'invoiceNo' | 'ewayBillNo'>(orderType === 'B2C' ? 'awbNo' : 'lrNo');
  const [searchVal, setSearchVal] = useState('');
  // Booking Failed tab: filter by the courier's failure reason — either picked from the grouped
  // reason list or typed as free text (both hit the same `failedReason` param).
  const [failedReason, setFailedReason] = useState('');
  const [merchantIds, setMerchantIds] = useState<(string | number)[]>([]);
  const { user: _au } = useAuth();
  const isMerchant = _au?.kind === 'merchant';
  const [courierIds, setCourierIds] = useState<(string | number)[]>([]);
  const [deliveryType, setDeliveryType] = useState('');
  const [startDate, setStartDate] = useState('');
  const [shipResult, setShipResult] = useState<ShipResult | null>(null);
  const [endDate, setEndDate] = useState('');
  const [datePreset, setDatePreset] = useState<DatePreset>('custom');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // Choose-Courier-&-Ship: lets the merchant pick from the couriers an admin assigned to them,
  // then books the selection. Offered on the tabs where a courier can still be changed.
  const [chooseCourier, setChooseCourier] = useState(false);
  // Bulk pickup-location assignment — channel/webhook orders arrive without one.
  const [assignWarehouse, setAssignWarehouse] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);
  const [actionsPos, setActionsPos] = useState<{ top: number; left: number } | null>(null);
  const actionsPanelRef = useRef<HTMLDivElement>(null);
  const [showUpload, setShowUpload] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [exportCols, setExportCols] = useState<string[]>([]); // default: all UNCHECKED
  const [exporting, setExporting] = useState(false);
  // B2B gets its own (larger, ops-defined) column set; B2C keeps the generic one.
  // Merchants may export everything EXCEPT the internal "booked by" user columns.
  const activeExportColumns = useMemo(() => {
    const base = orderType === 'B2B' ? B2B_EXPORT_COLUMNS : EXPORT_COLUMNS;
    return isMerchant ? base.filter(c => c.key !== 'userId' && c.key !== 'userName') : base;
  }, [orderType, isMerchant]);

  const actionsRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const h = (e: MouseEvent) => {
      const tgt = e.target as Node;
      if (actionsRef.current && actionsRef.current.contains(tgt)) return;
      if (actionsPanelRef.current && actionsPanelRef.current.contains(tgt)) return;
      setActionsOpen(false);
    };
    const recompute = () => {
      if (!actionsRef.current) return;
      const r = actionsRef.current.getBoundingClientRect();
      const left = Math.max(8, r.right - 256);
      setActionsPos({ top: r.bottom + 4, left });
    };
    if (actionsOpen) {
      recompute();
      window.addEventListener('resize', recompute);
      window.addEventListener('scroll', recompute, true);
    }
    document.addEventListener('click', h);
    return () => {
      document.removeEventListener('click', h);
      window.removeEventListener('resize', recompute);
      window.removeEventListener('scroll', recompute, true);
    };
    // MUST depend on `actionsOpen`. With an empty array this ran once at mount, when actionsOpen was
    // still false, so `recompute()` never fired and `actionsPos` stayed null forever — and the panel
    // renders only on `actionsOpen && actionsPos`. The result was a More Actions button that flipped
    // its chevron and opened nothing, on both B2C and B2B.
  }, [actionsOpen]);

  const { data: merchants } = useQuery({
    queryKey: ['merchants', 'all-for-orders'],
    queryFn: async () => (await api.get<Merchant[]>('/merchants')).data,
    staleTime: 1000 * 60 * 5,
  });
  const { data: couriers } = useQuery({
    queryKey: ['couriers', `${orderType}-active`],
    queryFn: async () => (await api.get<Courier[]>('/couriers', { params: { orderType, isActive: 'true' } })).data,
    staleTime: 1000 * 60 * 5,
  });

  // "Failed orders" view. Today Uploads no longer has a Failed sub-filter — it lists shipped rows
  // only, and every failed booking lives in this tab.
  const isFailedTab = tab === 'booking-failed';

  // Grouped failure reasons (reason + how many orders have it) for the reason dropdown. Only fetched
  // while a failed view is open; scoped to the same merchant/courier/date filters as the list.
  const { data: failedReasonOptions } = useQuery({
    queryKey: ['order-failed-reasons', orderType, merchantIds, courierIds, startDate, endDate],
    enabled: isFailedTab,
    queryFn: async () => {
      const params: any = { orderType };
      if (merchantIds.length) params.merchantIds = merchantIds.join(',');
      if (courierIds.length) params.courierIds = courierIds.join(',');
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      return (await api.get<{ total: number; items: { label: string; searchTerm: string; count: number }[] }>('/orders/failed-reasons', { params })).data;
    },
    staleTime: 60_000,
  });

  // The active list filters, shared by the list query AND the export so export always matches the list.
  const buildFilterParams = () => {
    const params: any = { orderType, tab };
    if (tab === 'all-shipped' && statusIds.length) params.statusIds = statusIds.join(',');
    if (searchVal) params[searchField] = searchVal;
    if (isFailedTab && failedReason.trim()) params.failedReason = failedReason.trim();
    if (merchantIds.length === 1) params.merchantId = merchantIds[0]; else if (merchantIds.length > 1) params.merchantIds = merchantIds.join(',');
    if (courierIds.length === 1) params.courierId = courierIds[0]; else if (courierIds.length > 1) params.courierIds = courierIds.join(',');
    if (deliveryType) params.deliveryType = deliveryType;
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    return params;
  };

  // Tab badge counts. These must describe the SAME rows the list is showing, so they take every
  // cross-tab filter (merchant, courier, delivery type, date range, search) but NOT the tab-specific
  // ones (`tab`, `statusIds`) — the endpoint returns one count per tab.
  // Previously only `orderType` was sent, so filtering to a single merchant left every badge showing
  // PLATFORM-WIDE totals: Millionaire Metric filtered → "Delivered 7,650" above a list of their 313.
  const countFilterParams = () => {
    const params: any = { orderType };
    if (searchVal) params[searchField] = searchVal;
    if (failedReason.trim()) params.failedReason = failedReason.trim();
    if (merchantIds.length === 1) params.merchantId = merchantIds[0]; else if (merchantIds.length > 1) params.merchantIds = merchantIds.join(',');
    if (courierIds.length === 1) params.courierId = courierIds[0]; else if (courierIds.length > 1) params.courierIds = courierIds.join(',');
    if (deliveryType) params.deliveryType = deliveryType;
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    return params;
  };
  const { data: tabCounts = {} as Record<string, number> } = useQuery({
    queryKey: ['order-tab-counts', { orderType, searchField, searchVal, failedReason, merchantIds, courierIds, deliveryType, startDate, endDate }],
    queryFn: async () => (await api.get<Record<string, number>>('/orders/tab-counts', { params: countFilterParams() })).data,
    refetchInterval: 60000,
  });

  const { data, isLoading } = useQuery({
    queryKey: ['orders', { orderType, tab, statusIds, searchField, searchVal, failedReason, merchantIds, courierIds, deliveryType, startDate, endDate, page, pageSize }],
    queryFn: async () => {
      const params = { ...buildFilterParams(), page, pageSize };
      return (await api.get<{ items: Order[]; total: number; page: number; pageSize: number }>('/orders', { params })).data;
    },
    placeholderData: (prev: any) => prev,
    // Poll every 12s while any B2B order is awaiting its LR (Delhivery async booking) so the row flips
    // from "Looking for LR…" to the LR automatically once the courier callback lands.
    refetchInterval: (query: any) => {
      const its: Order[] = query?.state?.data?.items ?? [];
      const pending = orderType === 'B2B' && its.some((o) => !!o.jobId && !o.lrNo);
      return pending ? 12000 : false;
    },
  });

  // Ship runs long enough (one paced courier call per order) to need a real per-chunk progress bar
  // rather than an indeterminate spinner — see ./bulk-progress. The remaining bulk actions
  // (cancel / recheck-status) are single `updateMany` statements and return instantly, so they stay on
  // the mutation's own `isPending`.
  const bulkProgress = useBulkProgress();
  const bulk = useMutation({
    mutationFn: async ({ action, ids }: { action: string; ids: string[] }) =>
      (await api.post(`/orders/bulk/${action}`, { ids })).data,
    onSuccess: (r: any, vars) => {
      setSelected(new Set());
      if (vars.action === 'ship') {
        // ALWAYS show the summary (total / shipped / failed) after a bulk ship — even when everything succeeded.
        const ok = r.ok ?? 0, failed = r.failed ?? 0;
        setShipResult({ ok, failed, total: ok + failed, errors: r.errors ?? [] });
        if (failed === 0) toast.success(`All ${ok} order(s) shipped`);
        else if (ok > 0) toast.success(`Ship: ${ok} done, ${failed} failed`);
        else toast.error(`Ship failed for ${failed} order(s)`);
      } else {
        toast.success(`${vars.action.replace(/-/g, ' ')}: ${r.ok ?? 0} done${r.failed ? ` · ${r.failed} failed` : ''}`);
      }
      if (r.note) toast.message(r.note);
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Action failed'),
    // Always refresh list + counts after a bulk action (ship/cancel/…), success or failure.
    onSettled: () => { refreshOrders(); },
  });

  // Ship is the ONE bulk action that runs for minutes (one paced courier call per order), so it goes
  // out in chunks with live progress instead of a single request the browser abandons after 30s.
  // cancel / recheck-status are single `updateMany` statements — they return instantly and stay as-is.
  const shipInChunks = async (ids: string[]) => {
    const errors: ShipResult['errors'] = [];
    await bulkProgress.run<string, any>({
      label: 'Shipping',
      items: ids,
      chunkSize: SHIP_CHUNK_SIZE,
      call: (chunk) => api.post('/orders/bulk/ship', { ids: chunk }, { timeout: CHUNK_TIMEOUT_MS }).then((r) => r.data),
      tally: (r) => ({ ok: r?.ok ?? 0, failed: r?.failed ?? 0 }),
      onChunk: (r) => { if (Array.isArray(r?.errors)) errors.push(...r.errors); },
      // A chunk whose REQUEST failed booked nothing we can attribute per order — record it against the
      // whole chunk so the summary total still adds up and the operator knows which orders to re-check.
      onChunkError: (err, chunk) => {
        const msg = err?.response?.data?.message ?? err?.message ?? 'Request failed';
        for (const id of chunk) errors.push({ id, source: 'system', error: msg });
      },
    });
    const failed = errors.length;
    const ok = ids.length - failed;
    setSelected(new Set());
    setShipResult({ ok, failed, total: ids.length, errors });
    if (failed === 0) toast.success(`All ${ok} order(s) shipped`);
    else if (ok > 0) toast.success(`Ship: ${ok} done, ${failed} failed`);
    else toast.error(`Ship failed for ${failed} order(s)`);
    refreshOrders();
  };

  const runBulk = (action: string) => {
    const ids = Array.from(selected);
    if (ids.length === 0) { toast.error('Select at least one order'); return; }
    setActionsOpen(false);
    if (action === 'ship') { void shipInChunks(ids); return; }
    bulk.mutate({ action, ids });
  };

  const runExport = async () => {
    if (exportCols.length === 0) { toast.error('Select at least one column to export'); return; }
    setExporting(true);
    try {
      // Export honours the SAME filters currently applied to the list, plus the chosen columns.
      // Emit columns in the chooser's canonical order (not tick order) so the CSV sequence is stable.
      const orderedCols = activeExportColumns.filter(c => exportCols.includes(c.key)).map(c => c.key);
      const params = { ...buildFilterParams(), columns: orderedCols.join(',') };
      // Through `downloadBlob`, NOT a bare `api.get`. The export now returns the FULL filtered set
      // instead of the first 10,000 rows, so a wide range is minutes of server work — and this call
      // was still on the client's 30-SECOND default, at which axios abandons the request and reports
      // a bare "Export failed" for a file the server went on to build perfectly. `downloadBlob`
      // carries the 15-minute timeout every other export on the platform already uses, and revokes
      // the object URL on the next tick (revoking it synchronously can abort the save in Chrome).
      await downloadBlob(
        `/orders/export?${new URLSearchParams(
          Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '') as any,
        ).toString()}`,
        `orders-${orderType.toLowerCase()}-${new Date().toISOString().slice(0, 10)}.csv`,
      );
      setShowExport(false);
    } catch (e: any) {
      // The server's own words, not "Export failed" — a blob response hides `data.message`, which is
      // why a permission refusal that names the exact permission to grant never reached anyone.
      toast.error(await blobErrorMessage(e, 'Export failed'));
    } finally {
      setExporting(false);
    }
  };


  /**
   * Download the selected labels as a ZIP of PDFs (one per order, named by AWB/LR) — for forwarding
   * to a merchant. Separate from generatePdf(), which opens the print preview.
   */
  const [zipping, setZipping] = useState(false);
  async function downloadLabelZip(opts?: { size?: string; template?: string }) {
    const ids = Array.from(selected);
    if (ids.length === 0) { toast.error('Select orders first'); return; }
    if (ids.length > 300) { toast.error('Maximum 300 orders per ZIP — download in batches'); return; }
    setZipping(true);
    const t = toast.loading(`Building ${ids.length} label PDF(s)… this can take a minute`);
    try {
      const r = await fetch(`${API_BASE_URL}/orders/labels/zip`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${tokenStore.get()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ orderIds: ids, size: opts?.size, template: opts?.template }),
      });
      if (!r.ok) {
        // The server sends JSON on failure and a zip on success — read whichever arrived.
        let msg = `Failed (${r.status})`;
        try { const j = await r.json(); msg = j?.message ?? msg; } catch { /* not json */ }
        toast.error(msg, { id: t });
        return;
      }
      const blob = await r.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `labels-${orderType.toLowerCase()}-${new Date().toISOString().slice(0, 10)}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${ids.length} label(s) as ZIP`, { id: t });
    } catch (e: any) {
      toast.error(e?.message ?? 'Download failed', { id: t });
    } finally {
      setZipping(false);
    }
  }

  async function generatePdf(kind: 'labels' | 'invoices' | 'waybills' | 'manifest', opts?: { size?: string; template?: string }) {
    const ids = Array.from(selected);
    if (ids.length === 0) { toast.error('Select orders first'); return; }
    // Labels can be bulk-printed up to 1000 (one combined PDF). Invoices: B2C 100 / B2B 250. Waybills 50.
    let lim = 250;
    if (kind === 'labels') lim = 1000;
    else if (kind === 'waybills') lim = 50;
    else if (orderType === 'B2C' && kind === 'invoices') lim = 100;
    if (ids.length > lim) { toast.error(`Maximum ${lim} orders at a time`); return; }
    const url = `${API_BASE_URL}/orders/${kind}`;
    const token = tokenStore.get();
    const body: any = { orderIds: ids };
    if (kind === 'labels') { body.size = opts?.size; body.template = opts?.template; }
    const r = await fetch(url, { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
    if (!r.ok) { toast.error(`Failed: ${r.status}`); return; }
    const html = await r.text();
    const w = window.open('', '_blank');
    if (!w) { toast.error('Pop-up blocked — allow pop-ups'); return; }
    w.document.open(); w.document.write(html); w.document.close();
  }

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const hasSelection = selected.size > 0;
  // Some destinations (Labels/Invoices/Waybills/Manifest) need a punched AWB/LR AND a shipped/in-transit status.
  // statusId 24 = Not Shipped, 21 = Booking Failed, 9 = Cancelled — these never show downloads even if awbNo/lrNo somehow exist.
  const NO_DOWNLOAD_STATUSES = [9, 21, 24];
  const hasShippedInSelection = useMemo(() => {
    if (!hasSelection) return false;
    return items.some(o => selected.has(o.id) && (!!o.awbNo || !!o.lrNo) && !NO_DOWNLOAD_STATUSES.includes(o.statusId));
  }, [items, selected, hasSelection]);

  // Delivered (8) / Partially Delivered (19) — the only states a proof of delivery exists for.
  const EPOD_STATUSES = [8, 19];
  const hasDeliveredInSelection = useMemo(() => {
    if (!hasSelection) return false;
    return items.some(o => selected.has(o.id) && EPOD_STATUSES.includes(o.statusId));
  }, [items, selected, hasSelection]);

  /**
   * Bulk POD download for B2C **and** B2B, through the POD service rather than the old
   * `orders/epod/bulk` route. That route only asked the courier for a signed POD, which exists for
   * Delhivery B2B alone — every B2C shipment came back "does not support E-POD download". The POD
   * service resolves whichever proof actually applies: the courier's own document where there is
   * one, the OTP-verified certificate for our own network, and a POD we generate for any carrier
   * that issues none. Comes back as a ZIP, with anything unavailable listed in `_NOT_INCLUDED.txt`
   * instead of failing the whole download.
   */
  const POD_MAX = 100;
  const [epodBusy, setEpodBusy] = useState(false);
  const downloadPods = async () => {
    const picked = items.filter(o => selected.has(o.id) && EPOD_STATUSES.includes(o.statusId));
    const numbers = picked.map(o => (orderType === 'B2B' ? (o.lrNo || o.awbNo) : (o.awbNo || o.lrNo))).filter(Boolean) as string[];
    if (!numbers.length) { toast.error('Select at least one delivered order'); return; }
    if (numbers.length > POD_MAX) { toast.error(`Maximum ${POD_MAX} PODs per download — select fewer`); return; }
    setEpodBusy(true);
    const t = toast.loading(`Preparing ${numbers.length} POD(s)…`);
    try {
      const r = await fetch(`${API_BASE_URL}/pod/bulk-zip`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${tokenStore.get()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ orderType, numbers }),
      });
      if (!r.ok) {
        let msg = `Failed (${r.status})`;
        try { const j = await r.json(); msg = j?.message ?? msg; } catch { /* not json */ }
        toast.error(msg, { id: t });
        return;
      }
      const blob = await r.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pods-${orderType.toLowerCase()}-${new Date().toISOString().slice(0, 10)}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${numbers.length} POD(s) — check _NOT_INCLUDED.txt for any skipped`, { id: t });
      qc.invalidateQueries({ queryKey: ['orders'] });
    } catch (e: any) {
      toast.error(e?.message ?? 'POD download failed', { id: t });
    } finally { setEpodBusy(false); }
  };

  const toggleAll = () => {
    if (items.every(i => selected.has(i.id))) setSelected(new Set());
    else setSelected(new Set(items.map(i => i.id)));
  };
  const toggleOne = (id: string) => {
    setSelected(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  };

  const SEARCH_FIELDS = orderType === 'B2C'
    ? [
        { id: 'awbNo' as const, label: 'AWB No.' },
        { id: 'orderNo' as const, label: 'Order No.' },
        { id: 'customerMobile' as const, label: 'Customer Mobile' },
      ]
    : [
        { id: 'lrNo' as const, label: 'LR No.' },
        { id: 'awbNo' as const, label: 'AWB No.' },
        { id: 'orderNo' as const, label: 'Order No.' },
        { id: 'customerMobile' as const, label: 'Customer Mobile' },
        { id: 'invoiceNo' as const, label: 'Invoice No.' },
        { id: 'ewayBillNo' as const, label: 'Eway Bill' },
      ];

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      {/* Retention notice. Shown on BOTH queues, for B2C and B2B, and to merchants and admins alike —
          the purge does not care who is looking, so neither should the warning. The day counts mirror
          OrderPurgeService (10d Not Shipped / 30d Booking Failed); keep them in step if those change. */}
      {(tab === 'not-shipped' || tab === 'booking-failed') && (
        <div className="mb-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800 flex items-start gap-2">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          <span>
            {tab === 'not-shipped'
              ? <>Not Shipped orders are automatically removed after <b>10 days</b>.</>
              : <>Booking Failed orders are automatically removed after <b>1 month</b>.</>}
            {' '}Orders that already have an AWB/LR or have been billed are never removed.
            Ship or cancel anything you want to keep.
          </span>
        </div>
      )}

      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold text-slate-900">Orders {orderType}</h1>
        <div className="flex items-center gap-2">
          <button onClick={() => nav(`/orders-${orderType.toLowerCase()}/new`)}
            className="bg-primary text-white px-3 py-2 text-sm rounded-lg hover:bg-primary-700 flex items-center gap-2">
            <Plus size={14} /> Create New Order
          </button>
          <button onClick={() => setShowUpload(true)}
            className="px-3 py-2 border border-slate-300 text-slate-700 text-sm rounded-lg hover:bg-slate-50 flex items-center gap-2">
            <Upload size={14} /> Upload Orders
          </button>
          <button onClick={() => setShowExport(true)} className="px-3 py-2 border border-slate-300 text-slate-700 text-sm rounded-lg hover:bg-slate-50 flex items-center gap-2">
            <Download size={14} /> Export Orders
          </button>
        </div>
      </div>

      {/* Redesigned action ribbon — selection count + primary buttons + Actions dropdown */}
      <div className="bg-white border border-slate-200 rounded-xl px-4 py-2.5 mb-4 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 pr-3 border-r border-slate-200">
          <span className={`text-xs px-2 py-1 rounded font-medium ${hasSelection ? 'bg-primary/10 text-primary' : 'bg-slate-100 text-slate-500'}`}>
            {selected.size} selected
          </span>
        </div>

        {/* Always-visible primary actions */}
        {(tab === 'not-shipped' || tab === 'booking-failed') && (
          <>
            <PrimaryBtn icon={<Truck size={14} />} label="Choose Courier & Ship"
              onClick={() => { if (!hasSelection) { toast.error('Select at least one order'); return; } setChooseCourier(true); }}
              disabled={!hasSelection} variant="primary" />
            <PrimaryBtn icon={<Warehouse size={14} />} label="Set Pickup Location"
              onClick={() => { if (!hasSelection) { toast.error('Select at least one order'); return; } setAssignWarehouse(true); }}
              disabled={!hasSelection} />
          </>
        )}
        {orderType === 'B2C' && (
          <>
            <PrimaryBtn icon={<Truck size={14} />} label="Ship" onClick={() => runBulk('ship')} disabled={!hasSelection || bulkProgress.running} variant="primary" />
            <PrimaryBtn icon={<FileText size={14} />} label="Download Labels" onClick={() => generatePdf('labels')} disabled={!hasShippedInSelection} />
            <PrimaryBtn icon={zipping ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} label={zipping ? 'Zipping…' : 'Labels ZIP (PDF)'} onClick={() => downloadLabelZip()} disabled={!hasShippedInSelection || zipping} />
            <PrimaryBtn icon={<FileText size={14} />} label="Download Invoices" onClick={() => generatePdf('invoices')} disabled={!hasShippedInSelection} />
          </>
        )}
        {orderType === 'B2B' && (
          <PrimaryBtn icon={zipping ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} label={zipping ? 'Zipping…' : 'Labels ZIP (PDF)'}
            onClick={() => downloadLabelZip({ size: '3x2', template: 'B2B_3X2' })} disabled={!hasShippedInSelection || zipping} />
        )}
        {/* Proof of delivery — B2C AND B2B, internal staff only. Only delivered shipments have one,
            so it stays disabled until the selection contains at least one. Merchants download PODs
            one shipment at a time (the per-row icon); the API refuses the bulk ZIP for them. */}
        {!isMerchant && (
          <PrimaryBtn icon={epodBusy ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
            label={epodBusy ? 'Preparing PODs…' : 'Download PODs'}
            onClick={() => downloadPods()} disabled={!hasDeliveredInSelection || epodBusy} />
        )}
        <PrimaryBtn icon={<Eye size={14} />} label="Recheck Status" onClick={() => runBulk('recheck-status')} disabled={!hasSelection} />
        <PrimaryBtn icon={<Trash2 size={14} />} label="Cancel" onClick={() => { if (confirm(`Cancel ${selected.size} order(s)?`)) runBulk('cancel'); }} disabled={!hasSelection} variant="danger" />

        {/* More Actions dropdown */}
        <div ref={actionsRef} className="relative ml-auto">
          <button onClick={(e) => { e.stopPropagation(); setActionsOpen(o => !o); }}
            disabled={!hasSelection && orderType === 'B2C' ? false : !hasSelection}
            className="px-3 py-1.5 text-sm border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 flex items-center gap-2">
            More Actions <ChevronDown size={14} className={`transition ${actionsOpen ? 'rotate-180' : ''}`} />
          </button>
          {actionsOpen && actionsPos && createPortal(
            <div ref={actionsPanelRef}
              style={{ position: 'fixed', top: actionsPos.top, left: actionsPos.left, width: 256, zIndex: 10100 }}
              className="bg-white border border-slate-200 rounded-lg shadow-xl py-1 text-sm">
              {orderType === 'B2C' && (
                <>
                  <DropdownGroup label="Documents">
{orderType === 'B2C' ? (<>
                    <DropdownItem icon={<FileText size={14} />} label="Generate Labels (4×6 default)" onClick={() => generatePdf('labels')} disabled={!hasShippedInSelection} />
                    <DropdownItem icon={<FileText size={14} />} label="Generate Invoices" onClick={() => generatePdf('invoices')} disabled={!hasShippedInSelection} />
                    <DropdownItem icon={<FileText size={14} />} label="Generate Manifest" onClick={() => generatePdf('manifest')} disabled={!hasShippedInSelection} />
                  </>) : (<>
                    <DropdownItem icon={<FileText size={14} />} label="B2B Labels — 3×2" onClick={() => generatePdf('labels', { size: '3x2', template: 'B2B_3X2' })} disabled={!hasShippedInSelection} />
                    <DropdownItem icon={<FileText size={14} />} label="B2B Labels — A4" onClick={() => generatePdf('labels', { template: 'B2B_A4' })} disabled={!hasShippedInSelection} />
                    <DropdownItem icon={<FileText size={14} />} label="Waybill Copy (3 copies)" onClick={() => generatePdf('waybills')} disabled={!hasShippedInSelection} />
                    <DropdownItem icon={<FileText size={14} />} label="Generate Invoices" onClick={() => generatePdf('invoices')} disabled={!hasShippedInSelection} />
                    <DropdownItem icon={<FileText size={14} />} label="Generate Manifest" onClick={() => generatePdf('manifest')} disabled={!hasShippedInSelection} />
                  </>)}
                    <DropdownItem icon={<Printer size={14} />} label="Generate Manifests" onClick={() => runBulk('generate-manifest')} disabled={!hasSelection} />
                  </DropdownGroup>
                  <DropdownGroup label="Edit">
                    <DropdownItem icon={<Edit3 size={14} />} label="Update AWB / Merchant" onClick={() => toast.message('Update AWB Merchant — coming soon')} />
                    <DropdownItem icon={<Upload size={14} />} label="Upload Status (CSV)" onClick={() => toast.message('Upload Status — coming soon')} />
                  </DropdownGroup>
                </>
              )}
              {orderType === 'B2B' && (
                <DropdownGroup label="Documents">
                  <DropdownItem icon={<Printer size={14} />} label="Generate Manifests" onClick={() => runBulk('generate-manifest')} disabled={!hasSelection} />
                </DropdownGroup>
              )}
            </div>,
            document.body
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200 mb-4 flex gap-1 overflow-x-auto">
        {TABS.map(t => (
          <button key={t.id} onClick={() => { setTab(t.id); setPage(1); setStatusIds([]); }}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition ${
              tab === t.id ? 'border-primary text-primary' : 'border-transparent text-slate-500 hover:text-slate-900'
            }`}>{t.label} {typeof tabCounts[t.id] === 'number' && (
              <span className={`ml-1 inline-flex items-center justify-center px-1.5 py-0 text-[10px] font-semibold rounded-full ${tab === t.id ? 'bg-primary text-white' : 'bg-slate-200 text-slate-600'}`}>{tabCounts[t.id]}</span>
            )}</button>
        ))}
      </div>

      {/* Today Uploads is shipped-only, so it says so rather than leaving the reader to wonder where
          the rest of today's rows went. The old All/Shipped/Failed toggle is gone: "All" mixed three
          queues into one tab, and "Failed" duplicated the Booking Failed tab. */}
      {tab === 'today-uploads' && (
        <div className="mb-4 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-800">
          Shows orders with <b>today's booking date</b> that were <b>successfully booked</b> (AWB/LR received).
          Bookings that failed are in <b>Booking Failed</b>; orders not yet shipped are in <b>Not Shipped</b>.
          Older orders imported today are <b>not</b> listed here — find them by booking date on <b>All Shipped Orders</b>.
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-1 border border-slate-200 rounded-lg overflow-hidden">
          <select value={searchField} onChange={(e) => setSearchField(e.target.value as any)}
            className="px-3 py-2 text-sm border-r border-slate-200 bg-slate-50 outline-none">
            {SEARCH_FIELDS.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
          </select>
          <input type="text" value={searchVal} onChange={(e) => setSearchVal(e.target.value)}
            placeholder={
              searchField === 'awbNo' || searchField === 'lrNo'
                ? `Enter ${searchField === 'awbNo' ? 'AWB' : 'LR'} no — comma-separated for many (max 200)`
                : `Search by ${SEARCH_FIELDS.find(f => f.id === searchField)?.label ?? ''}`
            }
            className="px-3 py-2 text-sm outline-none w-80" />
        </div>
        {!isMerchant && (
        <div className="min-w-[240px]">
          <MultiSelectFilter label="" placeholder="Select Merchants" values={merchantIds}
            onChange={setMerchantIds}
            options={(merchants ?? []).map(m => ({ value: m.id, label: m.companyName + ' (#' + m.id + ')' }))} />
        </div>
        )}
        <div className="min-w-[240px]">
          <MultiSelectFilter label="" placeholder="Select Couriers" values={courierIds}
            onChange={setCourierIds}
            options={(couriers ?? []).map(c => ({ value: c.id, label: c.name + ' (#' + c.id + ')' }))} />
        </div>
        {tab === 'all-shipped' && (
        <div className="min-w-[240px]">
          <MultiSelectFilter label="" placeholder="Filter by Status" values={statusIds}
            onChange={(v) => { setStatusIds(v); setPage(1); }}
            options={ALL_SHIPPED_STATUS_IDS.map(id => ({ value: id, label: STATUS_LABEL[id] ?? `Status ${id}`, hint: '#' + id }))} />
        </div>
        )}
        <select value={deliveryType} onChange={(e) => setDeliveryType(e.target.value)}
          className="px-3 py-2 rounded-lg border border-slate-300 text-sm outline-none focus:border-primary bg-slate-50">
          <option value="">Delivery Type</option>
          <option value="COD">COD</option>
          <option value="PREPAID">Prepaid</option>
        </select>
        {tab !== 'today-uploads' && tab !== 'not-shipped' && (
          <div className="w-56">
            <DateRangePicker value={{ startDate, endDate }} defaultPreset={datePreset}
              onChange={(r) => { setStartDate(r.startDate); setEndDate(r.endDate); setDatePreset(r.preset); setPage(1); }} />
          </div>
        )}
        {/* Failure-reason filter — only on the failed views (B2C + B2B). Pick a grouped reason, or
            type any words from the courier's message. */}
        {isFailedTab && (
          <div className="flex items-center gap-1 border border-rose-200 bg-rose-50/50 rounded-lg overflow-hidden">
            <select value={failedReasonOptions?.items.some(o => o.searchTerm === failedReason) ? failedReason : ''}
              onChange={(e) => { setFailedReason(e.target.value); setPage(1); }}
              title="Filter by booking failure reason"
              className="px-3 py-2 text-sm bg-white border-r border-rose-200 outline-none max-w-[320px]">
              <option value="">All failure reasons{failedReasonOptions ? ` (${failedReasonOptions.total})` : ''}</option>
              {(failedReasonOptions?.items ?? []).map((o) => (
                <option key={o.searchTerm + o.label} value={o.searchTerm} title={o.label}>
                  {o.label.length > 70 ? o.label.slice(0, 70) + '…' : o.label} · {o.count}
                </option>
              ))}
            </select>
            <input type="text" value={failedReason} onChange={(e) => { setFailedReason(e.target.value); setPage(1); }}
              placeholder="or type any words from the error"
              className="px-3 py-2 text-sm outline-none w-56 bg-white" />
            {failedReason && (
              <button onClick={() => { setFailedReason(''); setPage(1); }} title="Clear reason filter"
                className="px-2 text-slate-400 hover:text-slate-700 bg-white self-stretch">✕</button>
            )}
          </div>
        )}
        <div className="ml-auto flex items-center gap-2">
          <button onClick={() => qc.invalidateQueries({ queryKey: ['orders'] })}
            className="px-3 py-2 border border-emerald-300 text-emerald-700 text-sm rounded-lg hover:bg-emerald-50 flex items-center gap-2">
            <Search size={14} /> Search
          </button>
          <button onClick={() => {
            setSearchVal(''); setMerchantIds([]); setCourierIds([]); setDeliveryType('');
            setStartDate(''); setEndDate(''); setStatusIds([]); setFailedReason(''); setPage(1);
          }} className="px-3 py-2 border border-slate-300 text-slate-700 text-sm rounded-lg hover:bg-slate-50">Reset</button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-4 py-2 border-b border-slate-100 text-xs text-slate-500">
          <span>{selected.size} Orders Selected</span>
          <span>Rows per page:&nbsp;
            <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }} className="text-xs border border-slate-200 rounded px-1 py-0.5">
              {Array.from({ length: 20 }, (_, i) => (i + 1) * 25).map(n => <option key={n} value={n}>{n}</option>)}
            </select>
            &nbsp;&nbsp;{((page - 1) * pageSize + 1).toLocaleString()}–{Math.min(page * pageSize, total).toLocaleString()} of {total.toLocaleString()}&nbsp;&nbsp;
            <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="px-1 disabled:opacity-30">‹</button>
            <button disabled={page * pageSize >= total} onClick={() => setPage(p => p + 1)} className="px-1 disabled:opacity-30">›</button>
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-emerald-100/60 text-xs uppercase text-emerald-900">
              <tr>
                <th className="px-3 py-2"><input type="checkbox" checked={items.length > 0 && items.every(i => selected.has(i.id))} onChange={toggleAll} className="rounded" /></th>
                {!isMerchant && <th className="text-left px-3 py-2">Merchant Name</th>}
                <th className="text-left px-3 py-2">{orderType === 'B2C' ? 'Order Name' : 'Invoice No'}</th>
                <th className="text-left px-3 py-2">Customer</th>
                <th className="text-left px-3 py-2">Tracking Details</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Delivered / RTO Delivered</th>
                <th className="text-left px-3 py-2">Warehouse</th>
                <th className="text-left px-3 py-2">Created At</th>
                {!isMerchant && <th className="text-left px-3 py-2">Uploaded By</th>}
                <th className="text-left px-3 py-2">{orderType === 'B2C' ? 'Payment Mode' : 'Order Date'}</th>
                <th className="text-left px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {/* Shimmer rows rather than a spinner: they hold the table's shape, so the header stays
                  aligned and nothing jumps when the page of orders lands. Row count matches a typical
                  page so the scrollbar does not lurch either. */}
              {isLoading && <TableSkeleton rows={10} cols={isMerchant ? 10 : 12} firstColNarrow />}
              {!isLoading && items.length === 0 && <tr><td colSpan={isMerchant ? 10 : 12} className="text-center py-12 text-slate-400">No orders in this tab.</td></tr>}
              {items.map(o => (
                <tr key={o.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-2"><input type="checkbox" checked={selected.has(o.id)} onChange={() => toggleOne(o.id)} className="rounded" /></td>
                  {!isMerchant && <td className="px-3 py-2 text-xs"><div className="font-medium text-slate-900">{o.merchant?.companyName ?? '—'}</div></td>}
                  <td className="px-3 py-2 text-xs">
                    {/* B2B: show the REAL invoice number (from invoiceDetails); B2C: the order name/no. */}
                    <span className="flex items-center gap-1">
                      <a onClick={() => nav(`/orders-${orderType.toLowerCase()}/${o.id}`)} className="text-primary hover:underline cursor-pointer">{orderType === 'B2B' ? (o.invoiceNo ?? o.orderNo) : o.orderNo}</a>
                      <CopyText value={(orderType === 'B2B' ? (o.invoiceNo ?? o.orderNo) : o.orderNo) ?? ''} display="" iconSize={10} silent />
                    </span>
                    {orderType === 'B2B' && o.invoiceNo && (
                      <div className="text-[10px] text-slate-500 mt-0.5">Order: <span className="font-mono">{o.orderNo}</span></div>
                    )}
                    {/* A B2B consignment routinely carries two or three invoices; the cell showed only
                        the first, so the rest were invisible outside the order detail page. */}
                    {orderType === 'B2B' && (o.invoiceNosAll?.length ?? 0) > 1 && (
                      <div className="text-[10px] text-slate-500 mt-0.5" title={o.invoiceNosAll!.join(', ')}>
                        + {o.invoiceNosAll!.length - 1} more invoice{o.invoiceNosAll!.length > 2 ? 's' : ''}:{' '}
                        <span className="font-mono">{o.invoiceNosAll!.slice(1).join(', ')}</span>
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    <div className="font-medium">{o.customerName}</div>
                    <div className="text-slate-500">{o.customerCity}, {o.customerState}</div>
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {((orderType === 'B2B' ? o.lrNo : o.awbNo) ?? (orderType === 'B2B' ? o.awbNo : o.lrNo)) ? (
                      <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-50 border border-emerald-200 font-mono font-medium text-emerald-800 text-[12px] tracking-tight">
                        <CopyText value={(orderType === 'B2B' ? (o.lrNo ?? o.awbNo) : (o.awbNo ?? o.lrNo)) ?? ''} display={<span>{orderType === 'B2B' ? (o.lrNo ?? o.awbNo) : (o.awbNo ?? o.lrNo)}</span>} iconSize={10} silent />
                      </div>
                    ) : (
                      <div className="text-slate-400">—</div>
                    )}
                    {o.courier?.name ? (
                      <span className="inline-flex items-center gap-1 mt-1 text-[11px] font-bold text-slate-900">
                        <Truck size={12} className="text-slate-800 shrink-0" />
                        {o.courier.name}
                      </span>
                    ) : (
                      <span className="text-slate-400 text-[10px]">No courier</span>
                    )}
                    {o.pickupId && (
                      <div className="mt-1 text-[10px] text-slate-600">
                        Pickup ID: <span className="font-mono font-semibold text-slate-800">{o.pickupId}</span>
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <button
                        type="button"
                        onClick={() => setTrackOrderId(o.id)}
                        className={`text-xs px-2 py-0.5 rounded border cursor-pointer hover:ring-2 hover:ring-primary/30 transition ${STATUS_COLOR[o.statusId] ?? 'bg-slate-100 text-slate-600 border-slate-200'}`}
                        title={o.statusId === 23 && !isMerchant && o.cancelledByName ? `Cancelled by ${o.cancelledByName} — click for tracking` : 'Click to view tracking'}
                      >
                        {STATUS_LABEL[o.statusId] ?? o.statusId}
                      </button>
                      {/* Cancellation trail — internal staff only, WHEN as well as WHO. Who cancelled
                          a shipment and at what time is an internal accountability record; a merchant
                          sees that the order is Cancelled, not who inside the company pressed the button.
                          The API also stops sending the fields to merchant callers, so this is the
                          display half of the rule, not the whole of it. */}
                      {!isMerchant && o.statusId === 23 && o.cancelledAt && (
                        <span
                          className="text-[10px] text-slate-500 whitespace-nowrap"
                          title={o.cancelledByName ? `Cancelled by ${o.cancelledByName}` : undefined}
                        >
                          {new Date(o.cancelledAt).toLocaleString(undefined, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        </span>
                      )}
                      {o.statusId === 21 && o.lastBookingError && (
                        <span className="text-[10px] text-rose-700 mt-0.5 max-w-[260px] truncate" title={o.lastBookingError}>
                          ⚠ {o.lastBookingError}
                        </span>
                      )}
                      {(o.totalAttempts ?? 0) > 0 && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 border border-rose-200" title={`${o.totalAttempts} delivery attempt(s)`}>
                          {o.totalAttempts}/3 attempts
                        </span>
                      )}
                    </div>
                  </td>
                  {/* Delivery dates. The backend sends `*Resolved` so shipments delivered before these
                      columns existed fall back to their last (terminal) scan instead of showing blank. */}
                  <td className="px-3 py-2 text-xs whitespace-nowrap">
                    {o.deliveredAtResolved ? (
                      <div className="text-emerald-700 font-medium" title="Delivered">
                        {new Date(o.deliveredAtResolved).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' })}
                      </div>
                    ) : null}
                    {o.rtoDeliveredAtResolved ? (
                      <div className="text-amber-700 font-medium" title="RTO Delivered">
                        RTO · {new Date(o.rtoDeliveredAtResolved).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' })}
                      </div>
                    ) : null}
                    {!o.deliveredAtResolved && !o.rtoDeliveredAtResolved && <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-600">{o.warehouse?.name ? <span title={`${o.warehouse.code ?? ''} ${o.warehouse.city ?? ''}`.trim()}>{o.warehouse.name}</span> : (o.warehouseId ? `#${o.warehouseId}` : '—')}</td>
                  <td className="px-3 py-2 text-xs text-slate-600 whitespace-nowrap">{o.createdAt ? new Date(o.createdAt).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' }) : '—'}</td>
                  {!isMerchant && <td className="px-3 py-2 text-xs text-slate-700 whitespace-nowrap">{o.createdByName ?? '—'}</td>}
                  <td className="px-3 py-2 text-xs">
                    {orderType === 'B2C'
                      ? <span className={`px-2 py-0.5 rounded text-xs font-medium ${o.deliveryType === 'COD' ? 'bg-amber-50 text-amber-700' : 'bg-green-50 text-green-700'}`}>{o.deliveryType}</span>
                      : new Date(o.bookingDate).toLocaleDateString('en-IN')}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <div className="flex items-center gap-1.5">
                      {(() => {
                        // For B2C, master tracking = AWB; for B2B = LR. A stray awbNo on a failed B2B
                        // shouldn't hide the Edit button — use the per-orderType field.
                        const hasNumber = orderType === 'B2B' ? !!o.lrNo : !!o.awbNo;
                        const isEditable = [21, 24].includes(o.statusId);
                        const isCancelled = o.statusId === 23;
                        // Delhivery B2B async booking: courier accepted the job but the LR arrives later
                        // via callback. Block Ship + show a "looking for LR" loader until it lands.
                        const awaitingLr = !!o.jobId && !hasNumber && orderType === 'B2B';
                        if (awaitingLr) {
                          // Show the pending loader, but KEEP Edit available (never Ship — re-shipping a
                          // queued job duplicates the order). Edit lets ops punch the LR manually from
                          // the Delhivery panel if the callback is delayed.
                          return (
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-amber-600 inline-flex items-center gap-1 whitespace-nowrap" title={`Booking queued (job ${o.jobId}). Waiting for LR from courier — do NOT re-ship (would duplicate). Punch the LR via Edit if you have it.`}>
                                <Loader2 size={12} className="animate-spin" /> Looking for LR…
                              </span>
                              <button onClick={() => nav(`/orders-${orderType.toLowerCase()}/${o.id}?edit=1`)}
                                className="text-xs bg-white text-amber-700 border border-amber-300 px-2 py-1 rounded hover:bg-amber-50 flex items-center gap-1">
                                <Edit3 size={12} /> Edit
                              </button>
                            </div>
                          );
                        }
                        // Edit button is always visible in editable statuses (21=Booking Failed, 24=Not Shipped)
                        if (isEditable || (!hasNumber && !isCancelled)) {
                          return (
                            <div className="flex items-center gap-1">
                              {!hasNumber && !isCancelled && (
                                <button onClick={() => bookMut.mutate(o.id)} disabled={bookMut.isPending}
                                  className="text-xs bg-primary text-white px-2 py-1 rounded hover:bg-primary/90 flex items-center gap-1 disabled:opacity-50">
                                  <Ticket size={12} /> Ship
                                </button>
                              )}
                              {isEditable && (
                                <button onClick={() => nav(`/orders-${orderType.toLowerCase()}/${o.id}?edit=1`)}
                                  className="text-xs bg-white text-amber-700 border border-amber-300 px-2 py-1 rounded hover:bg-amber-50 flex items-center gap-1">
                                  <Edit3 size={12} /> Edit
                                </button>
                              )}
                            </div>
                          );
                        }
                        // Has waybill → Cancel button in pre-shipment statuses
                        if (hasNumber && [1,2,20,21,24].includes(o.statusId)) {
                          return (
                            <button onClick={() => { setCancelTargetId(o.id); setCancelPw(''); setCancelPwModalOpen(true); }} disabled={cancelMut.isPending}
                              className="text-xs bg-white text-rose-600 border border-rose-200 px-2 py-1 rounded hover:bg-rose-50 flex items-center gap-1 disabled:opacity-50">
                              <XCircle size={12} /> Cancel
                            </button>
                          );
                        }
                        // Book Appointment is Delhivery B2B only (Delhivery is the only courier whose API supports it)
                        if (orderType === 'B2B' && o.courier?.code?.toLowerCase()?.startsWith('delhivery')) {
                          return (
                            <button onClick={() => bookApptMut.mutate(o.id)} disabled={bookApptMut.isPending}
                              className="text-xs text-primary border border-primary/30 px-2 py-1 rounded hover:bg-primary/10 flex items-center gap-1 disabled:opacity-50">
                              <CheckCircle2 size={12} />BOOK AN APPOINTMENT
                            </button>
                          );
                        }
                        // Click-to-call (delivered / in-transit rows land here). This used to be a
                        // <button> with NO onClick — it looked like a link and did nothing at all. Now it
                        // is the same CallButton the order detail page uses, so it dials via /ivr/call.
                        return o.customerMobile
                          ? <CallButton toNumber={o.customerMobile} orderId={o.id} awbNo={o.awbNo} lrNo={o.lrNo}
                              reason={STATUS_LABEL[o.statusId] ?? 'Manual'} label="Click to Call" />
                          : <span className="text-xs text-slate-400" title="This order has no customer mobile">No mobile</span>;
                      })()}
                      {/* A stored POD opens straight away. A delivered row with nothing stored yet
                          still gets a button — the POD service produces one on demand (courier's
                          own where it exists, otherwise generated), which is why this is no longer
                          hidden behind `o.podFile`. */}
                      {/* Calls & recording for THIS shipment. Sits outside the IIFE above so it is
                          not swallowed by whichever action branch won — a B2B Delhivery row shows
                          BOOK AN APPOINTMENT instead of Click to Call, and would otherwise lose the
                          call history too. Admin only: `/ivr/*` is 403 for a merchant token. */}
                      {!isMerchant && (o.awbNo || o.lrNo) && (
                        <CallRecordingsButton orderId={o.id} awbNo={o.awbNo} lrNo={o.lrNo} />
                      )}
                      {o.podFile ? (
                        <SignedFileLink href={o.podFile} download downloadName={(orderType === 'B2B' ? (o.lrNo || o.awbNo) : (o.awbNo || o.lrNo)) ?? 'pod'} title="Download POD"
                          className="text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 p-1 rounded hover:bg-emerald-100">
                          <Download size={12} />
                        </SignedFileLink>
                      ) : EPOD_STATUSES.includes(o.statusId) && (o.awbNo || o.lrNo) ? (
                        <PodButton orderType={orderType} number={(orderType === 'B2B' ? (o.lrNo || o.awbNo) : (o.awbNo || o.lrNo))!} />
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Modal isOpen={showUpload} onClose={() => setShowUpload(false)} title={`Upload ${orderType} Orders`} subtitle="Bulk import via CSV / Excel" maxWidth="3xl">
        <OrderUploadDialog orderType={orderType} onClose={() => setShowUpload(false)} />
      </Modal>

      <Modal isOpen={showExport} onClose={() => setShowExport(false)} title={`Export ${orderType} Orders`} subtitle="Choose the columns to include. The current filters are applied." maxWidth="2xl">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 text-sm font-medium text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                checked={exportCols.length === activeExportColumns.length}
                ref={(el) => { if (el) el.indeterminate = exportCols.length > 0 && exportCols.length < activeExportColumns.length; }}
                onChange={(e) => setExportCols(e.target.checked ? activeExportColumns.map(c => c.key) : [])}
              />
              Select All
            </label>
            <span className="text-xs text-slate-500">{exportCols.length} of {activeExportColumns.length} selected</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2 max-h-[46vh] overflow-y-auto border rounded-lg p-3">
            {activeExportColumns.map(col => (
              <label key={col.key} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={exportCols.includes(col.key)}
                  onChange={(e) => setExportCols(prev => e.target.checked ? [...prev, col.key] : prev.filter(k => k !== col.key))}
                />
                {col.label}
              </label>
            ))}
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={() => setShowExport(false)} className="px-4 py-2 border border-slate-300 text-slate-700 text-sm rounded-lg hover:bg-slate-50">Cancel</button>
            <button onClick={runExport} disabled={exporting || exportCols.length === 0}
              className="px-4 py-2 bg-primary text-white text-sm rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2">
              {exporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
              Export {exportCols.length ? `(${exportCols.length})` : ''}
            </button>
          </div>
        </div>
      </Modal>
      {/* Bulk-ship progress — chunked, so this shows real "N of M" movement rather than a spinner that
          looks frozen for ten minutes and gets the button pressed again. */}
      {bulkProgress.progress && <BulkProgressOverlay progress={bulkProgress.progress} />}
      {assignWarehouse && (
        <AssignWarehouseDialog
          ids={Array.from(selected)}
          onClose={() => setAssignWarehouse(false)}
          onDone={() => { setSelected(new Set()); refreshOrders(); }}
        />
      )}

      {chooseCourier && (
        <ChooseCourierDialog
          ids={Array.from(selected)}
          onClose={() => setChooseCourier(false)}
          onShipped={(r) => {
            setSelected(new Set());
            // Reuse the SAME summary modal a normal bulk ship shows, so the outcome reads identically.
            setShipResult({ ok: r.ok, failed: r.failed, total: r.ok + r.failed, errors: r.errors ?? [] });
            if (r.failed === 0 && r.ok > 0) toast.success(`All ${r.ok} order(s) shipped`);
            else if (r.ok > 0) toast.success(`Ship: ${r.ok} done, ${r.failed} failed`);
            else if (r.failed > 0) toast.error(`Ship failed for ${r.failed} order(s)`);
            refreshOrders();
          }}
        />
      )}

      {shipResult && (
        <div className="fixed inset-0 bg-black/50 z-[10001] flex items-center justify-center p-4" onClick={() => setShipResult(null)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3 border-b flex items-center justify-between">
              <h2 className="text-lg font-semibold">Ship Summary</h2>
              <button onClick={() => setShipResult(null)} className="text-slate-400 hover:text-slate-700 text-xl leading-none">×</button>
            </div>
            {/* Summary stat row — total / shipped / failed */}
            <div className="grid grid-cols-3 divide-x divide-slate-200 text-center border-b border-slate-200">
              <div className="p-3"><div className="text-xs text-slate-500">Total Orders</div><div className="text-2xl font-bold text-slate-900">{shipResult.total ?? (shipResult.ok + shipResult.failed)}</div></div>
              <div className="p-3 bg-emerald-50"><div className="text-xs text-emerald-700">Shipped</div><div className="text-2xl font-bold text-emerald-700">{shipResult.ok}</div></div>
              <div className="p-3 bg-rose-50"><div className="text-xs text-rose-700">Failed</div><div className="text-2xl font-bold text-rose-700">{shipResult.failed}</div></div>
            </div>
            <div className="p-5 overflow-y-auto flex-1">
              {shipResult.errors.length === 0 && <div className="text-emerald-700 text-sm">All selected orders shipped successfully.</div>}
              {shipResult.errors.length > 0 && (
                <div className="space-y-2">
                  {shipResult.errors.map((e: any, i: number) => (
                    <div key={i} className={`border rounded p-3 text-sm ${e.source === 'courier' ? 'border-rose-200 bg-rose-50' : 'border-amber-200 bg-amber-50'}`}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="font-medium">
                          {e.orderType === 'B2B'
                            ? <>Invoice: <span className="font-mono">{e.invoiceNo ?? e.orderNo ?? `#${e.id}`}</span></>
                            : <>Order No: <span className="font-mono">{e.orderNo ?? `#${e.id}`}</span></>}
                        </div>
                        <span className={`text-xs px-2 py-0.5 rounded ${e.source === 'courier' ? 'bg-rose-200 text-rose-900' : 'bg-amber-200 text-amber-900'}`}>
                          {e.source === 'courier' ? 'Courier error → moved to Booking Failed' : 'System error'}
                        </span>
                      </div>
                      <div className="text-xs text-slate-700">{e.error}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="px-5 py-3 border-t bg-slate-50 flex justify-end">
              <button onClick={() => setShipResult(null)} className="px-4 py-2 bg-emerald-600 text-white rounded text-sm">Close</button>
            </div>
          </div>
        </div>
      )}
    {cancelPwModalOpen && (
        <Modal isOpen onClose={() => setCancelPwModalOpen(false)} title="Confirm Cancellation — re-enter your password">
          <div className="space-y-3">
            <p className="text-sm text-slate-600">To cancel this order, please re-enter your login password.</p>
            <input
              type="password"
              autoFocus
              placeholder="Your password"
              value={cancelPw}
              onChange={(e) => setCancelPw(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:border-primary"
              onKeyDown={(e) => { if (e.key === 'Enter' && cancelPw && cancelTargetId) {
                setCancelPwBusy(true);
                cancelMut.mutate({ orderId: cancelTargetId, password: cancelPw }, {
                  onSettled: () => { setCancelPwBusy(false); setCancelPwModalOpen(false); setCancelPw(''); setCancelTargetId(null); }
                });
              }}}
            />
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => { setCancelPwModalOpen(false); setCancelPw(''); setCancelTargetId(null); }}
                className="px-3 py-1.5 border border-slate-300 text-slate-700 text-sm rounded-lg hover:bg-slate-50">Back</button>
              <button type="button" disabled={!cancelPw || cancelPwBusy} onClick={() => {
                if (!cancelTargetId) return;
                setCancelPwBusy(true);
                cancelMut.mutate({ orderId: cancelTargetId, password: cancelPw }, {
                  onSettled: () => { setCancelPwBusy(false); setCancelPwModalOpen(false); setCancelPw(''); setCancelTargetId(null); }
                });
              }} className="bg-danger text-white px-3 py-1.5 text-sm rounded-lg disabled:opacity-50">
                {cancelPwBusy ? 'Verifying…' : 'Confirm Cancel'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      <TrackingDrawer orderId={trackOrderId} onClose={() => setTrackOrderId(null)} />
    </div>
  );
}

function PrimaryBtn({ icon, label, onClick, disabled, variant = 'default' }: { icon: React.ReactNode; label: string; onClick: () => void; disabled?: boolean; variant?: 'primary' | 'danger' | 'default' }) {
  const styles = {
    primary: 'bg-primary text-white hover:bg-primary-700 border border-transparent',
    danger: 'bg-white text-danger border border-red-200 hover:bg-red-50',
    default: 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-50',
  }[variant];
  return (
    <button onClick={onClick} disabled={disabled}
      className={`${styles} px-3 py-1.5 text-sm rounded-lg flex items-center gap-1.5 transition disabled:opacity-50 disabled:cursor-not-allowed`}>
      {icon} {label}
    </button>
  );
}
function DropdownGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="py-1">
      <div className="px-3 py-1 text-[10px] uppercase tracking-wide text-slate-400 font-semibold">{label}</div>
      {children}


    </div>
  );
}
function DropdownItem({ icon, label, onClick, disabled }: { icon: React.ReactNode; label: string; onClick: () => void; disabled?: boolean }) {
  return (
    <button onClick={onClick} disabled={disabled}
      className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center gap-2 disabled:opacity-50">
      {icon} {label}
    </button>
  );
}

/**
 * Per-row proof-of-delivery button for a delivered shipment that has nothing stored yet.
 *
 * The POD is produced on demand — the courier's signed document where one exists, otherwise a POD we
 * generate from the delivery scan — so the button has to ask the server before it has a link. It
 * opens the result in a new tab, and says plainly when the shipment genuinely has no proof
 * available rather than failing silently.
 */
function PodButton({ orderType, number }: { orderType: 'B2C' | 'B2B'; number: string }) {
  const [busy, setBusy] = useState(false);
  const open = async () => {
    setBusy(true);
    try {
      // Streamed through our API as an attachment — see /pod/download. A presigned storage URL
      // opens in a tab and cannot be made to save, because `download` is ignored cross-origin.
      await downloadBlob(`/pod/download?orderType=${orderType}&number=${encodeURIComponent(number)}`, number);
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? 'Could not fetch the POD');
    } finally { setBusy(false); }
  };
  return (
    <button type="button" onClick={open} disabled={busy} title="Download POD"
      className="text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 p-1 rounded hover:bg-emerald-100 disabled:opacity-50">
      {busy ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
    </button>
  );
}
